<template>
  <VTDropdownPill
    :disable-save-button="disableSaveButton"
    :is-enabled="isEditable"
    @confirm="handleSave"
    @close="closeDropdown = false"
    @clear="handleReset"
    @click="handlePropagation"
    @on-click-outside="handleReset"
  >
    <template #dropDownHeader>
      <VTPill label="Triggers">
        <template #values>
          <div :class="$style.wrapper">
            <div>
              <div :class="$style.row">
                <div :class="[$style.valuesContainer, $style.timeText]">
                  <Chip v-for="trigger in displayedTriggers" :key="trigger.id" :label="trigger.name" class="p-mr-2" />
                  <Chip v-if="hasMore" :label="`+${remainingCount}`" class="p-mr-2" />
                </div>
              </div>
            </div>
          </div>
        </template>
      </VTPill>
    </template>
    <template #dropdown-content>
      <TriggerManager
        :selected-triggers="selectedTriggers"
        :game-id="gameId"
        :class="$style.triggerManager"
        @update-trigger="handleTriggerUpdate"
        @add-trigger="handleAddTrigger"
        @delete-trigger="handleDeleteTrigger"
      />
    </template>
  </VTDropdownPill>
</template>

<script setup>
import TriggerManager from "@/components/TriggerManager";
import VTDropdownPill from "@/components/VTDropdownPill";
import VTPill from "@/components/VTPill/VTPill.vue";
import Chip from "primevue/chip";
import { computed, ref } from "vue";

const props = defineProps({
  triggers: {
    type: Array,
    default: () => [],
  },
  gameId: {
    type: String,
    required: true,
  },
  isEditable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["update:selectedTriggers"]);

const selectedTriggers = ref(props.triggers.map((trigger) => ({ name: trigger, id: trigger })));

const disableSaveButton = computed(() => selectedTriggers.value.length === 0);

const displayedTriggers = computed(() => selectedTriggers.value.slice(0, 2));
const remainingCount = computed(() => Math.max(selectedTriggers.value.length - 2, 0));
const hasMore = computed(() => selectedTriggers.value.length > 2);

const handleTriggerUpdate = (trigger) => {
  const index = selectedTriggers.value.findIndex((t) => t.id === trigger.value);
  if (index !== -1) {
    selectedTriggers.value.splice(index, 1);
  } else {
    selectedTriggers.value.push({ name: trigger.name, id: trigger.value });
  }
};

const handleAddTrigger = (trigger) => {
  selectedTriggers.value.push(trigger);
};

const handleDeleteTrigger = (trigger) => {
  const index = selectedTriggers.value.findIndex((t) => t.id === trigger.id);
  if (index !== -1) {
    selectedTriggers.value.splice(index, 1);
  }
};

function handlePropagation(event) {
  event.stopPropagation();
}

async function handleSave() {
  emit("update:selectedTriggers", selectedTriggers.value);
}

function handleReset() {
  selectedTriggers.value = props.triggers.map((trigger) => ({ name: trigger, id: trigger }));
}
</script>

<style module>
.triggerSection {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.chip {
  background-color: var(--vd-color-blue-light-9);
  border-radius: 8px;
}

.label {
  font-size: 14px;
  font-weight: 600;
  color: var(--vd-color-text-light-2);
}

.triggerManager {
  width: 300px;
}
</style>
